/**
 * @fileoverview Generic debounce hook for stabilizing rapidly changing values.
 * @module shared/hooks/useDebounce
 */

import { useEffect, useState } from 'react'

/**
 * Debounces a value by the specified delay, returning the stabilized value.
 *
 * @template T
 * @param value The input value to debounce
 * @param delayMs Debounce delay in milliseconds
 * @returns The debounced value that updates after the delay has passed
 * @example
 * ```ts
 * const debouncedQuery = useDebounce(query, 300);
 * ```
 */
export function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value)

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(handle)
  }, [value, delayMs])

  return debounced
}
